import type { ReactNode } from "react";

import { AlbumCard } from "@/components/album-card";
import type { AlbumGridAlbum } from "@/components/album-grid";
import type { LinkTarget } from "@/components/link";
import type {
  OfferSummaryBadgeMode,
  OfferSummaryBadgeVariant,
} from "@/components/marketplace/offer-summary-badge";
import type { ListingMap } from "@/hooks/use-listing-counts";
import { cn } from "@/lib/utils";

function itemClass<TAlbum>(
  value: string | ((album: TAlbum) => string | undefined) | undefined,
  album: TAlbum,
): string | undefined {
  if (typeof value === "function") return value(album);
  return value;
}

function listingDetails(listingMap: ListingMap | undefined, albumId: string) {
  const listing = listingMap?.get(albumId);
  return {
    listingCount: listing?.count,
    listingMinPrice: listing?.minPrice,
    listingMaxPrice: listing?.maxPrice,
    listingCurrency: listing?.currency ?? undefined,
  };
}

export function AlbumGridItem<TAlbum extends AlbumGridAlbum>(props: {
  album: TAlbum;
  footer?: (album: TAlbum) => ReactNode;
  hideActions: boolean;
  index: number;
  imageSizes?: string;
  itemClassName?: string | ((album: TAlbum) => string | undefined);
  link: (album: TAlbum) => LinkTarget;
  linkCoversFooter: boolean;
  listingMap?: ListingMap;
  offerSummaryMode?: OfferSummaryBadgeMode;
  offerSummaryVariant?: OfferSummaryBadgeVariant;
  priorityCount: number;
  showAlbumType: boolean;
  showOfferSummary: boolean;
  subtitle: (album: TAlbum) => string | undefined;
  tags?: (album: TAlbum) => ReactNode;
}) {
  const { album } = props;
  return (
    <li className={cn("min-w-0", itemClass(props.itemClassName, album))}>
      <AlbumCard
        {...props.link(album)}
        {...listingDetails(props.listingMap, album.id)}
        imageUrl={album.imageUrl}
        imageSizes={props.imageSizes}
        hideActions={props.hideActions}
        title={album.title}
        subtitle={props.subtitle(album)}
        releaseId={album.releaseId}
        workId={album.workId ?? album.id}
        addToListsTarget={album.addToListsTarget}
        priority={props.index < props.priorityCount}
        albumType={props.showAlbumType ? album.type : null}
        offerSummary={props.showOfferSummary ? album.offerSummary : null}
        offerSummaryByCondition={props.showOfferSummary ? album.offerSummaryByCondition : undefined}
        offerSummaryMode={props.offerSummaryMode}
        offerSummaryVariant={props.offerSummaryVariant}
        tags={props.tags?.(album)}
        footer={props.footer?.(album)}
        linkCoversFooter={props.linkCoversFooter}
      />
    </li>
  );
}
